import { createSlice } from "@reduxjs/toolkit";
import { isNicheId } from "../constants/niches";
import { logout } from "./authSlice";

const ACTIVE_NICHE_KEY = "activeNiche";
const NICHE_PICKER_DISMISSED_KEY = "nichePickerDismissed";

function readStoredNiche() {
    try {
        const stored = localStorage.getItem(ACTIVE_NICHE_KEY);
        return stored && isNicheId(stored) ? stored : null;
    } catch {
        return null;
    }
}

function readPickerDismissed() {
    try {
        return localStorage.getItem(NICHE_PICKER_DISMISSED_KEY) === "true";
    } catch {
        return false;
    }
}

const initialState = {
    /** @type {string | null} Niche id from a niche landing page visit */
    activeNiche: readStoredNiche(),
    /** User skipped niche selection via "Other" */
    nichePickerDismissed: readPickerDismissed(),
};

const nicheSlice = createSlice({
    name: "niche",
    initialState,
    reducers: {
        setActiveNiche: (state, action) => {
            const niche = action.payload;
            state.activeNiche = niche && isNicheId(niche) ? niche : null;
            state.nichePickerDismissed = false;
            try {
                if (state.activeNiche) {
                    localStorage.setItem(ACTIVE_NICHE_KEY, state.activeNiche);
                    localStorage.removeItem(NICHE_PICKER_DISMISSED_KEY);
                } else {
                    localStorage.removeItem(ACTIVE_NICHE_KEY);
                }
            } catch {
                /* ignore storage errors */
            }
        },
        dismissNichePicker: (state) => {
            state.nichePickerDismissed = true;
            try {
                localStorage.setItem(NICHE_PICKER_DISMISSED_KEY, "true");
            } catch {
                /* ignore storage errors */
            }
        },
        clearActiveNiche: () => {
            try {
                localStorage.removeItem(ACTIVE_NICHE_KEY);
                localStorage.removeItem(NICHE_PICKER_DISMISSED_KEY);
            } catch {
                /* ignore storage errors */
            }
            return {
                activeNiche: null,
                nichePickerDismissed: false,
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logout, (state) => {
            state.activeNiche = null;
            state.nichePickerDismissed = false;
            try {
                localStorage.removeItem(ACTIVE_NICHE_KEY);
                localStorage.removeItem(NICHE_PICKER_DISMISSED_KEY);
            } catch {
                /* ignore storage errors */
            }
        });
    },
});

export const { setActiveNiche, dismissNichePicker, clearActiveNiche } = nicheSlice.actions;

export const selectActiveNiche = (state) => state.niche.activeNiche;

/** Re-read niche from localStorage (falls back when Redux is briefly empty). */
export function readActiveNicheFromStorage() {
    return readStoredNiche();
}

export const selectShowNichePicker = (state) =>
    !state.niche.activeNiche && !state.niche.nichePickerDismissed;

export default nicheSlice.reducer;
